// Help.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "./Help.css";

export default function Help() {
  const [searchTerm, setSearchTerm] = useState("");
  const [openCategory, setOpenCategory] = useState(null);
  const [openQuestion, setOpenQuestion] = useState(null);

  const categories = [
    {
      id: "general",
      icon: "❓",
      title: "General",
      questions: [
        {
          q: "¿Qué es Wayo?",
          a: "Wayo es una aplicación de nutrición inteligente que usa IA para escanear tus comidas, calcular calorías automáticamente y ayudarte a alcanzar tus objetivos de salud. Simplemente toma una foto de tu comida y Wayo hace el resto."
        },
        {
          q: "¿Cómo creo mi cuenta?",
          a: "Descarga la app desde App Store o Google Play, haz click en 'Iniciar', sigue las instrucciones, introduce tu email y crea una contraseña. ¡Y listo!"
        },
        {
          q: "¿Es gratis usar Wayo?",
          a: "Wayo tiene una versión gratuita con funciones básicas. La suscripción Premium desbloquea análisis nutricional avanzado, recetas personalizadas, sin anuncios y más funciones."
        },
        {
          q: "¿Necesito acceso a internet para usar Wayo?",
          a: "Necesitas internet para escanear alimentos (usa IA en la nube). Los datos se sincronizan automáticamente cuando te conectes."
        }
      ]
    },
    {
      id: "account",
      icon: "📱",
      title: "Cuenta y Perfil",
      questions: [
        {
          q: "¿Cómo cambio mi contraseña?",
          a: "Ve a Configuración → Cuenta → Cambiar contraseña. Introduce tu contraseña actual y la nueva. Si olvidaste tu contraseña, usa 'Olvidé mi contraseña' en la pantalla de login."
        },
        {
          q: "¿Puedo cambiar mi email?",
          a: "Sí, ve a Configuración → Cuenta → Email. Introduce tu nuevo email y recibirás un código de verificación para confirmar el cambio."
        },
        {
          q: "¿Cómo actualizo mis datos personales (peso, altura, objetivos)?",
          a: "Ve a Configuración → Mi Perfil. Ahí puedes actualizar tu peso actual, altura, edad, objetivo (perder peso, ganar músculo, mantenimiento) y nivel de actividad."
        },
        {
          q: "¿Puedo tener varias cuentas?",
          a: "Cada email solo puede estar asociado a una cuenta. Si necesitas gestionar varias personas, considera usar emails diferentes para cada perfil."
        }
      ]
    },
    {
      id: "scanning",
      icon: "📸",
      title: "Escaneo de Alimentos",
      questions: [
        {
          q: "¿Cómo escaneo una comida?",
          a: "Abre Wayo, ve a 'Escanear', apunta la cámara a tu plato y toma una foto clara. La IA analizará los ingredientes y calorías automáticamente."
        },
        {
          q: "¿Qué precisión tiene el escaneo?",
          a: "El escaneo es 85-95% preciso. Para máxima precisión, toma fotos claras desde arriba, con buena iluminación y mostrando toda la comida."
        },
        {
          q: "¿Puedo editar los resultados del escaneo?",
          a: "Sí, después de escanear puedes ajustar porciones, ingredientes o calorías manualmente si lo necesitas."
        },
        {
          q: "¿Funciona con comida casera?",
          a: "Sí, funciona con cualquier comida: casera, restaurante, platos mixtos, etc. Mientras sea visible en la foto, Wayo puede analizarla."
        }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setOpenCategory(openCategory === categoryId ? null : categoryId);
    setOpenQuestion(null);
  };

  const toggleQuestion = (categoryId, questionIndex) => {
    const key = `${categoryId}-${questionIndex}`;
    setOpenQuestion(openQuestion === key ? null : key);
  };

  // Filtrar categorías y preguntas según búsqueda
  const filteredCategories = categories.map(category => {
    const filteredQuestions = category.questions.filter(qa =>
      qa.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
      qa.a.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return {
      ...category,
      questions: filteredQuestions
    };
  }).filter(category => category.questions.length > 0);

  return (
    <>
      <Helmet>
        <title>Ayuda - Wayo | Preguntas Frecuentes y Soporte</title>
        <meta name="description" content="Encuentra respuestas a las preguntas más frecuentes sobre Wayo. Guías de uso, consejos y soporte técnico." />
        <link rel="canonical" href="https://wayoai.app/help" />
      </Helmet>
      <main className="help-page">
        <Header />
        <section className="help-content">
          <div className="help-container">
            {/* Header */}
            <div className="help-header">
              <h1 className="help-title">Centro de Ayuda</h1>
              <p className="help-subtitle">
                Encuentra respuestas rápidas a las preguntas más comunes
              </p>
            </div>

            {/* Buscador */}
            <div className="help-search">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Busca tu pregunta..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                {searchTerm && (
                  <button 
                    className="search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            {/* Categorías */}
            <div className="help-categories">
              {filteredCategories.length > 0 ? (
                filteredCategories.map((category) => (
                  <div key={category.id} className="category-section">
                    <button
                      className={`category-header ${openCategory === category.id ? 'active' : ''}`}
                      onClick={() => toggleCategory(category.id)}
                    >
                      <span className="category-icon">{category.icon}</span>
                      <span className="category-title">{category.title}</span>
                      <span className="category-count">
                        {category.questions.length} {category.questions.length === 1 ? 'pregunta' : 'preguntas'}
                      </span>
                      <span className="category-arrow">
                        {openCategory === category.id ? '▼' : '▶'}
                      </span>
                    </button>

                    {openCategory === category.id && (
                      <div className="questions-list">
                        {category.questions.map((qa, index) => {
                          const questionKey = `${category.id}-${index}`;
                          const isOpen = openQuestion === questionKey;

                          return (
                            <div key={index} className="question-item">
                              <button
                                className={`question-header ${isOpen ? 'active' : ''}`}
                                onClick={() => toggleQuestion(category.id, index)}
                              >
                                <span className="question-text">{qa.q}</span>
                                <span className="question-arrow">
                                  {isOpen ? '−' : '+'}
                                </span>
                              </button>

                              {isOpen && (
                                <div className="answer-content">
                                  <p>{qa.a}</p>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="no-results">
                  <p className="no-results-icon">🔍</p>
                  <p className="no-results-text">
                    No encontramos resultados para "{searchTerm}"
                  </p>
                  <p className="no-results-hint">
                    Intenta con otras palabras clave o navega por las categorías
                  </p>
                </div>
              )}
            </div>

            {/* Sección de ayuda adicional */}
            <div className="help-footer">
              <div className="help-cta">
                <h3>¿No encuentras lo que buscas?</h3>
                <p>Nuestro equipo está aquí para ayudarte</p>
                <Link to="/contact" className="cta-button">
                  Contactar Soporte
                </Link>
              </div>

              <div className="help-links">
                <h4>Enlaces útiles</h4>
                <div className="links-grid">
                  <Link to="/privacy" className="help-link">
                    📄 Política de Privacidad
                  </Link>
                  <Link to="/terms" className="help-link">
                    📋 Términos y Condiciones
                  </Link>
                  <a 
                    href="mailto:soporte@wayoai.app" 
                    className="help-link"
                  >
                    📧 soporte@wayoai.app
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
        <Footer />
      </main>
    </>
  );
}
